import { useState, type FormEvent, type ReactNode } from 'react'
import type { PostValue } from '../api/client'
import type { ProtecteeBackup, TreeNode } from '../api/protectee'
import { SettingsCheck } from '../ui/SettingsParts'

/**
 * Piesele comune ale formularelor de backup - ale unei SARCINI (fila Backup
 * settings din All Devices) si ale unui PROFIL (Settings > Protectee):
 * algoritmul, arborele de dosare si citirea lor.
 *
 * ALGORITMUL e formularul din `backup/others/taskAlgorithmForm.php` /
 * `taskDetails.php` / `protectee_settings/.../editProfileDetails.php`: patru
 * file - Daily, Weekly, Monthly, Only Once - fiecare cu bifa ei Active, si
 * TOATE trimise deodata (`algorithm[<tip>][<camp>]`). O sarcina poate avea
 * deci si o rulare zilnica si una lunara. GFS si Custom au fisierele lor in
 * `algorithm_form/`, dar filele lor sunt comentate in toate trei machetele:
 * nu se ofera nici aici pentru scriere.
 *
 * DAR NU SE PIERD. Salvarea sterge algoritmul vechi si il scrie pe cel trimis
 * (`deleteAlgorithm` + `insertAlgorithmRows`), deci un GFS sau un Custom scris
 * de dinainte disparea la prima salvare a filei Daily. Acum pleaca inapoi
 * neschimbate, in campuri ascunse, si se vad pe filele lor, numai de citit
 * (decizia Roxanei, 18 septembrie 2026).
 *
 * "Start datetime" e data SI ora (`datetimepicker`, `yyyy-mm-dd hh:ii`) - ora
 * la care porneste backupul. Se trimite in acelasi format, `2026-09-18 14:30`.
 */

export const ALGORITHMS: { type: number; label: string }[] = [
  { type: 0, label: 'Only once' },
  { type: 1, label: 'By day' },
  { type: 2, label: 'By week' },
  { type: 3, label: 'By month' },
  { type: 4, label: 'GFS' },
  { type: 5, label: 'Custom' },
]

/** Filele formularului, in ordinea din machete. */
const TABS: { type: number; label: string }[] = [
  { type: 1, label: 'Daily' },
  { type: 2, label: 'Weekly' },
  { type: 3, label: 'Monthly' },
  { type: 0, label: 'Only Once' },
]

/** Tipurile care nu se scriu din formular, dar se pastreaza: GFS si Custom. */
const KEPT: { type: number; label: string }[] = [
  { type: 4, label: 'GFS' },
  { type: 5, label: 'Custom' },
]

/** Campurile fara de care un tip inceput nu e un tip (validatorii `formValidation` din protectee.js). */
const REQUIRED: Record<number, string[]> = {
  0: ['start_date'],
  1: ['recurrence', 'start_date'],
  2: ['recurrence', 'day_of_week', 'start_date'],
  3: ['recurrence', 'day_of_month', 'start_date'],
}

/** `by_month.php`: recurenta se alege din cinci, ziua din 1-31 sau Last (32). */
const MONTH_STEPS = [1, 2, 3, 6, 12]

/** Listele de care are nevoie formularul de algoritm (le da si `backup`, si fisa profilului din Settings > Protectee). */
export type AlgorithmLists = Pick<ProtecteeBackup, 'days_of_week' | 'months' | 'order_types'>

/** `2026-09-18 14:30[:00]` sau `2026-09-18` -> valoarea unui `datetime-local`. */
export function toLocalInput(value: string | undefined): string {
  const v = (value ?? '').trim()
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(v)
  if (!m) return ''
  return `${m[1]}T${m[2] ?? '00'}:${m[3] ?? '00'}`
}

/** Valoarea unui `datetime-local` -> `yyyy-mm-dd hh:ii`, formatul din portal. */
export function fromLocalInput(value: string): string {
  return value.replace('T', ' ').slice(0, 16)
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}><span className="color">{label}</span></label>
      <div className="col-sm-6">{children}</div>
    </div>
  )
}

function AlgorithmPane({ type, values, lists }: { type: number; values: Record<string, string>; lists: AlgorithmLists }) {
  const n = (f: string) => `algorithm[${type}][${f}]`
  const v = (f: string) => values[f] ?? ''
  const start = (
    <Row label="Start datetime">
      <div className="input-group">
        <input type="datetime-local" className="form-control" name={n('start_date')} defaultValue={toLocalInput(values.start_date)} />
        <span className="input-group-addon"><i className="fa fa-calendar"></i></span>
      </div>
    </Row>
  )
  const monthSteps = MONTH_STEPS.includes(Number(v('recurrence'))) || v('recurrence') === '' ? MONTH_STEPS : [...MONTH_STEPS, Number(v('recurrence'))]
  return (
    <>
      <div className="form-group">
        <div className="col-sm-6 col-sm-offset-3">
          <SettingsCheck name={n('active')} value="1" defaultChecked={v('active') === '1'} label="Active" />
        </div>
      </div>
      {type === 1 ? (
        <Row label="Recur every">
          <div className="input-group">
            <input type="text" className="form-control" name={n('recurrence')} defaultValue={v('recurrence')} inputMode="numeric" />
            <span className="input-group-addon">day(s)</span>
          </div>
        </Row>
      ) : null}
      {type === 2 ? (
        <>
          <Row label="Recur every">
            <div className="input-group">
              <input type="text" className="form-control" name={n('recurrence')} defaultValue={v('recurrence')} inputMode="numeric" />
              <span className="input-group-addon">week(s) on:</span>
            </div>
          </Row>
          <Row label="Day of week">
            <select className="form-control" name={n('day_of_week')} defaultValue={v('day_of_week')}>
              <option value="">Select</option>
              {Object.entries(lists.days_of_week).map(([k, d]) => <option key={k} value={k}>{d}</option>)}
            </select>
          </Row>
        </>
      ) : null}
      {type === 3 ? (
        <>
          <Row label="Recur every">
            <select className="form-control" name={n('recurrence')} defaultValue={v('recurrence')}>
              <option value="">Select</option>
              {monthSteps.map((m) => <option key={m} value={m}>{m === 1 ? '1 month' : `${m} months`}</option>)}
            </select>
          </Row>
          <Row label="Day of month">
            <select className="form-control" name={n('day_of_month')} defaultValue={v('day_of_month')}>
              <option value="">Select</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              <option value="32">Last</option>
            </select>
          </Row>
        </>
      ) : null}
      {start}
    </>
  )
}

/** Ce e gresit in algoritmul trimis - textele din portal - sau null. */
export function algorithmProblem(fields: Record<string, PostValue>): string | null {
  const get = (t: number, f: string) => String(fields[`algorithm[${t}][${f}]`] ?? '').trim()
  /* Un GFS sau un Custom pastrat e tot un algoritm: sarcina care are numai
     unul din ele nu e "fara algoritm". */
  const kept = Object.keys(fields).some((k) => /^algorithm\[[45]\]\[(?!active\])/.test(k) && String(fields[k] ?? '').trim() !== '')
  if (!kept && get(1, 'recurrence') === '' && get(2, 'recurrence') === '' && get(3, 'recurrence') === '' && get(0, 'start_date') === '') {
    return 'You must set at least one type of algorithm, in order to continue!'
  }
  for (const tab of TABS) {
    const need = REQUIRED[tab.type]!
    const filled = need.filter((f) => get(tab.type, f) !== '')
    if (filled.length > 0 && filled.length < need.length) {
      return `${tab.label}: please fill in all the fields, or none of them.`
    }
  }
  const recur = [get(1, 'recurrence'), get(2, 'recurrence')].filter((r) => r !== '')
  if (recur.some((r) => !/^[1-9]\d*$/.test(r))) return 'Recur every must be a whole number, 1 or more.'
  return null
}

/** Formularul de algoritm: cele patru file, toate trimise deodata - plus GFS si Custom, pastrate asa cum sunt. */
export function AlgorithmForm({ backup, algorithm, onSubmit, submitLabel }: {
  backup: AlgorithmLists
  algorithm: Record<string, Record<string, string>>
  onSubmit(fields: Record<string, PostValue>): void
  submitLabel: string
}) {
  const [tab, setTab] = useState(1)
  const [problem, setProblem] = useState<string | null>(null)
  const kept = KEPT.filter((t) => algorithm[String(t.type)])
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fields = formFields(e.currentTarget)
    for (const key of Object.keys(fields)) {
      /* Numai filele cu `datetime-local`; GFS si Custom pleaca exact cum au venit. */
      if (/^algorithm\[[0-3]\]\[start_date\]$/.test(key)) fields[key] = fromLocalInput(String(fields[key] ?? ''))
    }
    const wrong = algorithmProblem(fields)
    setProblem(wrong)
    if (wrong === null) onSubmit(fields)
  }
  return (
    <form className="form-horizontal labelcustomize tk-action-form taskAlgorithm" onSubmit={submit}>
      {problem !== null ? <div className="alert alert-danger">{problem}</div> : null}
      <ul role="tablist" className="nav nav-tabs">
        {[...TABS, ...kept].map((t) => (
          <li key={t.type} role="presentation" className={tab === t.type ? 'active' : undefined}>
            <a href="#" role="tab" aria-selected={tab === t.type} data-algorithm={t.type} onClick={(e) => { e.preventDefault(); setTab(t.type) }}>{t.label}</a>
          </li>
        ))}
      </ul>
      {/* Toate filele stau in formular - ascunse, nu scoase - ca sa plece toate la trimitere, ca in portal. */}
      <div className="tab-content" style={{ paddingTop: 15 }}>
        {TABS.map((t) => (
          <div key={t.type} role="tabpanel" className={`tab-pane${tab === t.type ? ' active in' : ''}`} style={{ display: tab === t.type ? 'block' : 'none' }}>
            <AlgorithmPane type={t.type} values={algorithm[String(t.type)] ?? {}} lists={backup} />
          </div>
        ))}
        {kept.map((t) => (
          <div key={t.type} role="tabpanel" className={`tab-pane algorithm-kept${tab === t.type ? ' active in' : ''}`} style={{ display: tab === t.type ? 'block' : 'none' }}>
            <div className="alert alert-info">
              {t.label} can no longer be set up here. It is kept exactly as it was saved, and goes back unchanged when you update the other types.
            </div>
            <table className="details-table" style={{ width: '100%' }}>
              <tbody>
                {describe(t.type, algorithm[String(t.type)]!, backup).map(([k, v]) => <tr key={k}><td className="title">{k}:</td><td>{v}</td></tr>)}
              </tbody>
            </table>
            {/* Campurile salvate, trimise inapoi neschimbate - altfel salvarea le-ar sterge. */}
            {Object.entries(algorithm[String(t.type)]!).map(([f, v]) => <input key={f} type="hidden" name={`algorithm[${t.type}][${f}]`} value={v} />)}
          </div>
        ))}
      </div>
      <button type="submit" className="btn btn-sm btn-primary">{submitLabel}</button>
    </form>
  )
}

/** Randurile unui tip, citite (`profileDetails.php`). */
function describe(type: number, f: Record<string, string>, lists: AlgorithmLists): [string, string][] {
  const rows: [string, string][] = [['Active', f.active === '1' ? 'yes' : 'no']]
  const list = (key: string, names: Record<string, string>) => {
    try {
      const j = JSON.parse(f[key] ?? '[]')
      return Array.isArray(j) ? j.map((x) => names[String(x)] ?? String(x)).join(', ') : ''
    } catch {
      return ''
    }
  }
  if (type === 1) rows.push(['Recur every', `${f.recurrence ?? ''} days`])
  if (type === 2) rows.push(['Recur every', `${f.recurrence ?? ''} weeks`], ['Day of week', lists.days_of_week[f.day_of_week ?? ''] ?? ''])
  if (type === 3) rows.push(['Recur every', `${f.recurrence ?? ''} months`], ['Day of month', f.day_of_month === '32' ? 'Last' : (f.day_of_month ?? '')])
  if (type === 4) rows.push(['Recur every', f.recurrence ?? ''], ['Path', f.path ?? ''])
  if (type === 5) {
    rows.push(['Months', list('recurrence', lists.months)])
    if (f.recurrence_type === '2') rows.push(['Days of week', list('days_of_week', lists.days_of_week)], ['Order', list('days_of_week_order', lists.order_types)])
    else rows.push(['Days of month', list('days_of_month', {})])
  }
  rows.push(['Start date', f.start_date ?? ''])
  return rows
}

/**
 * Algoritmul CITIT - `backup/others/profileDetails.php` si ramura "numai
 * citire" din Settings > Protectee: o fila pe tip, "This settings are not
 * defined" cand tipul lipseste. GFS si Custom apar numai daca profilul chiar
 * le are (scrise de dinainte de a fi scoase din formular).
 */
export function AlgorithmView({ algorithm, lists }: { algorithm: Record<string, Record<string, string>>; lists: AlgorithmLists }) {
  const tabs = [...TABS, ...(algorithm['4'] ? [{ type: 4, label: 'GFS' }] : []), ...(algorithm['5'] ? [{ type: 5, label: 'Custom' }] : [])]
  const [tab, setTab] = useState(tabs.find((t) => algorithm[String(t.type)])?.type ?? 1)
  const current = algorithm[String(tab)]
  return (
    <div className="algorithm-view">
      <ul role="tablist" className="nav nav-tabs">
        {tabs.map((t) => (
          <li key={t.type} role="presentation" className={tab === t.type ? 'active' : undefined}>
            <a href="#" role="tab" aria-selected={tab === t.type} onClick={(e) => { e.preventDefault(); setTab(t.type) }}>{t.label}</a>
          </li>
        ))}
      </ul>
      <div style={{ paddingTop: 10 }}>
        {current ? (
          <table className="details-table" style={{ width: '100%' }}>
            <tbody>
              {describe(tab, current, lists).map(([k, v]) => <tr key={k}><td className="title">{k}:</td><td>{v}</td></tr>)}
            </tbody>
          </table>
        ) : (
          <div className="alert alert-info"><strong>This settings are not defined</strong></div>
        )}
      </div>
    </div>
  )
}

/** Campurile unui formular, cu `x[]` ca liste si `a[b][c]` pastrat ca nume (encodePost le duce ca atare). */
export function formFields(form: HTMLFormElement): Record<string, PostValue> {
  const out: Record<string, PostValue> = {}
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value !== 'string') continue
    if (key.endsWith('[]')) {
      const base = key.slice(0, -2)
      const prev = out[base]
      out[base] = Array.isArray(prev) ? [...(prev as string[]), value] : [value]
    } else {
      out[key] = value
    }
  }
  return out
}

/* ----------------------------------------------------------------- arbore */

/**
 * Arborele de dosare, pliabil ca in Explorer / zTree din portal. Un dosar cu
 * subdosare are in fata un buton `+`/`-`: `+` cand e strans, `-` cand e desfacut;
 * clicul il pliaza/desface. Un dosar-frunza n-are buton, dar are un spatiu de
 * aceeasi latime, ca eticheta sa stea aliniata cu a celor cu buton.
 *
 * IMPORTANT: subdosarele stranse NU se scot din DOM, doar se ascund (`hidden`) -
 * casutele lor (chiar si cele adanci) trebuie sa ramana in formular, ca sa
 * pastreze bifa salvata si sa plece la trimitere. Butonul `+`/`-` e un `<button>`,
 * niciodata un `<input type="checkbox">`: casutele arborelui sunt numai dosarele.
 *
 * Implicit totul e strans; se desfac doar dosarele care au dedesubt un dosar
 * bifat (samanta din `expandSeed`), ca o sarcina/un profil salvat sa-si arate
 * alegerile. Pliajul e independent de bifa: nici togglul nu misca bifa, nici
 * bifa nu misca togglul. `disabled` (fisa numai de citit) lasa pliajul liber -
 * poti umbla prin arbore - dar tine casutele blocate.
 */
export function Tree({ nodes, checked, onToggle, disabled }: { nodes: TreeNode[]; checked: Set<string>; onToggle(node: TreeNode, on: boolean): void; disabled?: boolean }) {
  const [expanded, setExpanded] = useState<Set<string>>(() => expandSeed(nodes, new Set()))
  const onExpand = (path: string) => setExpanded((s) => {
    const next = new Set(s)
    if (next.has(path)) next.delete(path); else next.add(path)
    return next
  })
  return <TreeLevel nodes={nodes} checked={checked} onToggle={onToggle} disabled={disabled} expanded={expanded} onExpand={onExpand} />
}

/** Un nivel al arborelui, recursiv. Pliajul (`expanded`/`onExpand`) vine de sus, ca sa fie unul singur pe tot arborele. */
function TreeLevel({ nodes, checked, onToggle, disabled, expanded, onExpand }: {
  nodes: TreeNode[]
  checked: Set<string>
  onToggle(node: TreeNode, on: boolean): void
  disabled?: boolean
  expanded: Set<string>
  onExpand(path: string): void
}) {
  return (
    <ul className="protectee-tree" style={{ listStyle: 'none', paddingLeft: 18 }}>
      {nodes.map((n) => {
        const hasChildren = n.children.length > 0
        const open = expanded.has(n.path)
        return (
          <li key={n.path}>
            <span className="protectee-tree-row" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {hasChildren ? (
                <button
                  type="button"
                  className="protectee-tree-toggle"
                  aria-label={open ? `Collapse ${n.name}` : `Expand ${n.name}`}
                  aria-expanded={open}
                  onClick={() => onExpand(n.path)}
                  style={{ flex: '0 0 auto', width: 16, padding: 0, border: 0, background: 'none', color: 'inherit', lineHeight: 1, cursor: 'pointer' }}
                >
                  <span aria-hidden="true" style={{ display: 'inline-block', width: 13, height: 13, lineHeight: '11px', textAlign: 'center', border: '1px solid #999', borderRadius: 2, fontSize: 11, fontWeight: 700, fontFamily: 'monospace' }}>{open ? '−' : '+'}</span>
                </button>
              ) : (
                <span className="protectee-tree-toggle" style={{ flex: '0 0 auto', width: 16 }} aria-hidden="true"></span>
              )}
              <SettingsCheck
                name="directory[]"
                value={n.path}
                checked={checked.has(n.path)}
                disabled={disabled}
                onChange={(on) => onToggle(n, on)}
                label={n.missing
                  ? <span style={{ color: '#b94a48' }}><i className="fa fa-exclamation-triangle" title="Negăsit pe dispozitiv"></i> {n.name} <small>(negăsit pe dispozitiv)</small></span>
                  : <><i className="fa fa-folder"></i> {n.name}</>}
              />
            </span>
            {/* Subdosarele stau mereu in DOM - doar ascunse cand dosarul e strans - ca bifele lor sa plece la trimitere. */}
            {hasChildren ? (
              <div hidden={!open}>
                <TreeLevel nodes={n.children} checked={checked} onToggle={onToggle} disabled={disabled} expanded={expanded} onExpand={onExpand} />
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

export function collect(nodes: TreeNode[], into: Set<string>): Set<string> {
  for (const n of nodes) { if (n.checked) into.add(n.path); collect(n.children, into) }
  return into
}

/** Un dosar are macar un descendent bifat? (Are sens sa-l deschidem ca sa se vada alegerea.) */
function hasCheckedDescendant(nodes: TreeNode[]): boolean {
  return nodes.some((n) => n.checked || hasCheckedDescendant(n.children))
}

/**
 * Samanta de pliaj: se deschid dosarele care au dedesubt un dosar bifat (deci
 * stramosii oricarei bife initiale), ca o sarcina/un profil salvat sa-si arate
 * selectiile. Restul raman stranse - lista scurta, ca in Explorer.
 */
export function expandSeed(nodes: TreeNode[], into: Set<string>): Set<string> {
  for (const n of nodes) {
    if (hasCheckedDescendant(n.children)) into.add(n.path)
    expandSeed(n.children, into)
  }
  return into
}

/** Calea dosarului si a tuturor celor de sub el. */
export function subtree(node: TreeNode, into: string[] = []): string[] {
  into.push(node.path)
  for (const c of node.children) subtree(c, into)
  return into
}

/**
 * Arborele de dosare. Ca zTree din portal (`chkboxType: {Y: 's', N: 's'}`):
 * bifa pe un dosar ii bifeaza si toate subdosarele, debifarea le debifeaza;
 * parintele nu se schimba. Fara niciun dosar ales nu se trimite nimic.
 */
export function TreeForm({ tree, onSubmit, submitLabel, onRefresh }: { tree: TreeNode[]; onSubmit(paths: string[]): void; submitLabel: string; onRefresh?: () => void }) {
  const [checked, setChecked] = useState<Set<string>>(() => collect(tree, new Set()))
  const [problem, setProblem] = useState(false)
  const toggle = (node: TreeNode, on: boolean) => setChecked((s) => {
    const next = new Set(s)
    for (const p of subtree(node)) { if (on) next.add(p); else next.delete(p) }
    return next
  })
  return (
    <form className="taskDirectoryTree" onSubmit={(e) => {
      e.preventDefault()
      if (checked.size === 0) { setProblem(true); return }
      setProblem(false)
      onSubmit([...checked])
    }}>
      {problem ? <div className="alert alert-danger">You must select at least one folder in order to continue!</div> : null}
      {tree.length === 0 ? (
        onRefresh ? (
          <div className="alert alert-info">
            Waiting for the agent to send the directory tree &mdash; it arrives on the device&apos;s next check-in.{' '}
            <button type="button" className="btn btn-xs btn-default" onClick={onRefresh}>
              <i className="fa fa-sync-alt"></i> Request again
            </button>
          </div>
        ) : (
          <div className="alert alert-info">The agent has not sent the directory tree of this device yet.</div>
        )
      ) : (
        <Tree nodes={tree} checked={checked} onToggle={toggle} />
      )}
      <button type="submit" className="btn btn-sm btn-primary">{submitLabel}</button>
    </form>
  )
}
